import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import DeviceCharge from '#component/device/dashboard/charge';
import DeviceEvent from '#component/device/dashboard/event';
import momentFormat from '#component/device/dashboard/mapbox/moment-format';
import measurementsIcon from '#component/icon/measurements';
import { getYoungestMetrics, isActiveDevice } from '#component/device/helper';
import useDeviceData from '#component/device/dashboard/mapbox/useDevicesData';
import { formatter } from '#component/shipment/helper';
import {
  TooltipContainer,
  StyledPreloader,
  ErrorText,
  TempContainer,
  ChargeContainer,
  StyledDivider,
  LabelContainer,
  ActiveInTitle,
  TimestampContainer,
  Timestamp,
  StyledDeviceIcon
} from './styles';
import { isUndefinedOrNull } from '#helper';

/**
 * Renders device info, metrics, events etc.
 * Fetches device info data.
 */

const DeviceInfoTooltip = props => {
  const { device: propsDevice = {}, account, meta } = props;
  const uids = useMemo(() => [propsDevice.uid], [propsDevice]);
  const { status, devices: fetchedDevices, error } = useDeviceData(uids);
  const loading = status !== 'succeeded';
  const device = { ...propsDevice, ...fetchedDevices[0] };

  const [temperature, humidity, battery] =
    getYoungestMetrics(device?.lastMetric?.youngest, account, ['temperature', 'humidity', 'battery'], true);

  const icon = meta.device?.find(m => m.name === device.type)?.icon;
  const isMetricsExist = Object.keys(temperature).length !== 0 ||
    Object.keys(humidity).length !== 0 ||
    Object.keys(battery).length !== 0;

  const isActive = isActiveDevice(device);

  return (
    <TooltipContainer>
      {loading ? (
        <StyledPreloader />
      ) : (
        <>
          <LabelContainer>
            <StyledDeviceIcon icon={icon} />
            <Typography color="textSecondary">
              {device.title || device.uid}
            </Typography>
          </LabelContainer>
          <TimestampContainer>
            <Timestamp>
              Located here
              {' '}
              {momentFormat(device.lastLocation.timestamp, account, 'dateTimePartial')}
            </Timestamp>
          </TimestampContainer>
          {error ? (
            <>
              <StyledDivider />
              <ErrorText>
                Cannot load more details...
              </ErrorText>
            </>
          ) : (
            <>
              {isMetricsExist && (
                <StyledDivider />)}
              {!isUndefinedOrNull(temperature?.value) && (
              <TempContainer component="p">
                Temperature:
                {measurementsIcon(temperature.identifier, device, temperature.value)}
                {`${temperature.value}${temperature.unit}`}
              </TempContainer>)}
              {!isUndefinedOrNull(temperature?.value) && (
              <TempContainer component="p">
                Humidity:
                {measurementsIcon(humidity.identifier, device, humidity.value)}
                {`${humidity.value}${humidity.unit}`}
              </TempContainer>)}
              {!isUndefinedOrNull(battery?.value) && (
              <ChargeContainer>
                <Typography component="p">Charge:</Typography>
                <DeviceCharge device={device} battery={battery} />
              </ChargeContainer>)}
              {device.lastEvent && Object.keys(device.lastEvent).length !== 0 && (
              <>
                <StyledDivider />
                <DeviceEvent device={device} showInTooltip />
              </>
              )}
              {isActive && (
              <>
                <StyledDivider />
                {Array.isArray(device.shipments) && device.shipments?.length && (
                <ActiveInTitle variant="body1" component="p">
                  Active in shipment
                  {device.shipments.length > 1 ? 's' : '' }
                  :&nbsp;
                  {device.shipments.map((s, i) => (
                    <React.Fragment key={`shipment${s?.uid}`}>
                      <span>{s.label ?? formatter(s?.uid)}</span>
                      {i + 1 !== device.shipments.length ? ', ' : ''}
                    </React.Fragment>
                  ))}
                </ActiveInTitle>
                )}
                {Array.isArray(device.appliances) && device.appliances?.length && (
                <ActiveInTitle variant="body1" component="p">
                  Active in application
                  {device.appliances.length > 1 ? 's' : '' }
                  :&nbsp;
                  {device.appliances.map((s, i) => (
                    <React.Fragment key={`application${s?.uid}`}>
                      <span>{s.label}</span>
                      {i + 1 !== device.appliances.length ? ', ' : ''}
                    </React.Fragment>
                  ))}
                </ActiveInTitle>
                )}
              </>
              )}
            </>
          )}
        </>
      )}
    </TooltipContainer>
  );
};

export default DeviceInfoTooltip;
